const FADE_DURATION = 200;
const FADE_EASING = 'ease';
const SWIPE_FADE = true;
const SWIPE_THRESHOLD = 40;
const TARGET_WAIT_MAX = 2000;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function isMainGallerySlideshow(el) {
  return (
    el instanceof HTMLElement &&
    el.parentElement?.closest('media-gallery') &&
    !el.closest('product-card, dialog, quick-add-dialog, zoom-dialog') &&
    !el.parentElement?.closest('slideshow-component')
  );
}

const activeFades = new WeakMap();
const pendingSelects = new WeakMap();

function finishActiveFade(slideshow) {
  const state = activeFades.get(slideshow);
  if (!state) return;
  state.animations.forEach((a) => a.cancel());
  state.overlay?.remove();
  activeFades.delete(slideshow);
}

const delay = (ms) => new Promise((r) => setTimeout(r, ms));

async function decodeImage(img, timeout) {
  if (!img) return false;
  try {
    await Promise.race([
      img.decode(),
      delay(timeout).then(() => Promise.reject(new Error('timeout'))),
    ]);
    return true;
  } catch (_) {
    return false;
  }
}

function resolveTargetSlide(slideshow, input) {
  const slides = slideshow.slides;
  if (!slides?.length) return null;
  let index;
  if (typeof input === 'number') index = input;
  else if (typeof input === 'string') index = parseInt(input, 10);
  else if (input && 'id' in input) {
    return slides.find((s) => s.getAttribute('slide-id') === input.id) || null;
  } else return null;
  if (Number.isNaN(index)) return null;
  const last = slides.length - 1;
  if (index < 0) index = slideshow.infinite ? last : 0;
  else if (index > last) index = slideshow.infinite ? 0 : last;
  return slides[index] || null;
}

function warmUpGallery(slideshow) {
  if (!slideshow || slideshow.__fadeWarmed) return;
  slideshow.__fadeWarmed = true;
  slideshow.querySelectorAll('slideshow-slide img').forEach((img) => {
    img.loading = 'eager';
    img.decode().catch(() => {});
  });
}

function warmUpOnIdle() {
  const run = () => {
    document.querySelectorAll('media-gallery slideshow-component').forEach((s) => {
      if (isMainGallerySlideshow(s)) warmUpGallery(s);
    });
  };
  if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}

async function createOverlay(slideshow, fromSlide) {
  if (!fromSlide.classList.contains('product-media-container--image')) return null;
  const img = fromSlide.querySelector('img');
  const container = slideshow.refs?.slideshowContainer;
  if (!img || !container || !img.complete) return null;

  const clone = new Image();
  clone.src = img.currentSrc || img.src;
  clone.alt = '';
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('data-gallery-fade-overlay', '');
  clone.inert = true;

  if (!(await decodeImage(clone, 300))) return null;

  const imgRect = img.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  if (!imgRect.width || !imgRect.height) return null;

  const cs = getComputedStyle(img);
  Object.assign(clone.style, {
    position: 'absolute',
    top: `${imgRect.top - containerRect.top}px`,
    left: `${imgRect.left - containerRect.left}px`,
    width: `${imgRect.width}px`,
    height: `${imgRect.height}px`,
    objectFit: cs.objectFit,
    objectPosition: cs.objectPosition,
    borderRadius: cs.borderRadius,
    margin: '0',
    maxWidth: 'none',
    maxHeight: 'none',
    pointerEvents: 'none',
    zIndex: '1',
  });

  container.appendChild(clone);
  return clone;
}

async function patch() {
  await customElements.whenDefined('slideshow-component');
  const Slideshow = customElements.get('slideshow-component');
  if (!Slideshow || Slideshow.prototype.__pdpFadePatched) return;

  const originalSelect = Slideshow.prototype.select;

  Slideshow.prototype.select = async function (input, event, options = {}) {
    const passThrough =
      !isMainGallerySlideshow(this) ||
      reducedMotion.matches ||
      options.animate === false ||
      this.disabled ||
      (this.visibleSlides?.length ?? 1) > 1;

    if (passThrough) return originalSelect.call(this, input, event, options);

    finishActiveFade(this);
    warmUpGallery(this);

    const token = {};
    pendingSelects.set(this, token);
    const isStale = () => pendingSelects.get(this) !== token;

    const fromSlide = this.slides?.[this.current];
    const targetSlide = resolveTargetSlide(this, input);
    if (targetSlide && targetSlide === fromSlide) return;

    const targetImg = targetSlide?.querySelector('img');
    if (targetImg) {
      targetImg.loading = 'eager';
      await decodeImage(targetImg, TARGET_WAIT_MAX);
    }
    if (isStale()) return;

    const overlay = fromSlide ? await createOverlay(this, fromSlide) : null;
    if (isStale()) {
      overlay?.remove();
      return;
    }

    await originalSelect.call(this, input, event, { ...options, animate: false });

    const toSlide = this.slides?.[this.current];
    if (!toSlide || toSlide === fromSlide) {
      overlay?.remove();
      return;
    }

    const animations = overlay
      ? [
          overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
            duration: FADE_DURATION,
            easing: FADE_EASING,
            fill: 'forwards',
          }),
        ]
      : [
          toSlide.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: FADE_DURATION,
            easing: FADE_EASING,
          }),
        ];

    activeFades.set(this, { overlay, animations });
    try {
      await Promise.all(animations.map((a) => a.finished));
    } catch (_) {}
    if (activeFades.get(this)?.animations === animations) {
      overlay?.remove();
      activeFades.delete(this);
    }
  };

  Slideshow.prototype.__pdpFadePatched = true;
  warmUpOnIdle();
}

function mostVisibleMedia(media) {
  let best = null;
  let bestArea = 0;
  for (const el of media) {
    const r = el.getBoundingClientRect();
    const w = Math.max(0, Math.min(r.right, window.innerWidth) - Math.max(r.left, 0));
    const h = Math.max(0, Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0));
    if (w * h > bestArea) {
      bestArea = w * h;
      best = el;
    }
  }
  return best;
}

async function createZoomOverlay(dialog, fromItem) {
  if (!fromItem.classList.contains('product-media-container--image')) return null;
  const img = fromItem.querySelector('img');
  if (!img || !img.complete) return null;

  const clone = new Image();
  clone.src = img.currentSrc || img.src;
  clone.alt = '';
  if (!(await decodeImage(clone, 300))) return null;

  const box = fromItem.getBoundingClientRect();
  const imgRect = img.getBoundingClientRect();
  if (!box.width || !box.height || !imgRect.width || !imgRect.height) return null;

  const wrap = document.createElement('div');
  wrap.setAttribute('aria-hidden', 'true');
  wrap.setAttribute('data-gallery-fade-overlay', '');
  wrap.inert = true;
  Object.assign(wrap.style, {
    position: 'fixed',
    top: `${box.top}px`,
    left: `${box.left}px`,
    width: `${box.width}px`,
    height: `${box.height}px`,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: '1',
    background: getComputedStyle(dialog).backgroundColor,
  });

  const cs = getComputedStyle(img);
  Object.assign(clone.style, {
    position: 'absolute',
    top: `${imgRect.top - box.top}px`,
    left: `${imgRect.left - box.left}px`,
    width: `${imgRect.width}px`,
    height: `${imgRect.height}px`,
    objectFit: cs.objectFit,
    objectPosition: cs.objectPosition,
    margin: '0',
    maxWidth: 'none',
    maxHeight: 'none',
    transform: 'none',
  });

  wrap.appendChild(clone);
  dialog.appendChild(wrap);
  return wrap;
}

async function patchZoom() {
  await customElements.whenDefined('zoom-dialog');
  const ZoomDialog = customElements.get('zoom-dialog');
  if (!ZoomDialog || ZoomDialog.prototype.__pdpFadePatched) return;

  const originalSelectThumbnail = ZoomDialog.prototype.selectThumbnail;

  ZoomDialog.prototype.selectThumbnail = async function (index, options = { behavior: 'smooth' }) {
    const dialog = this.refs?.dialog;
    const media = this.refs?.media;
    const passThrough =
      reducedMotion.matches ||
      options?.behavior !== 'smooth' ||
      !dialog?.open ||
      !media?.length ||
      !this.closest('media-gallery') ||
      this.closest('product-card, quick-add-dialog');

    if (passThrough) return originalSelectThumbnail.call(this, index, options);

    const target = media[index];
    const from = mostVisibleMedia(media);
    if (!target || !from || target === from) {
      return originalSelectThumbnail.call(this, index, options);
    }

    finishActiveFade(this);

    const token = {};
    pendingSelects.set(this, token);
    const isStale = () => pendingSelects.get(this) !== token;

    const targetImg = target.querySelector('img');
    if (targetImg) {
      targetImg.loading = 'eager';
      await decodeImage(targetImg, TARGET_WAIT_MAX);
    }
    if (isStale()) return;

    const overlay = await createZoomOverlay(dialog, from);
    if (isStale()) {
      overlay?.remove();
      return;
    }

    await originalSelectThumbnail.call(this, index, { ...options, behavior: 'instant' });

    if (!dialog.open) {
      overlay?.remove();
      return;
    }

    const animations = overlay
      ? [
          overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
            duration: FADE_DURATION,
            easing: FADE_EASING,
            fill: 'forwards',
          }),
        ]
      : [
          target.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: FADE_DURATION,
            easing: FADE_EASING,
          }),
        ];

    activeFades.set(this, { overlay, animations });
    try {
      await Promise.all(animations.map((a) => a.finished));
    } catch (_) {}
    if (activeFades.get(this)?.animations === animations) {
      overlay?.remove();
      activeFades.delete(this);
    }
  };

  ZoomDialog.prototype.__pdpFadePatched = true;
}

function setupSwipeFade() {
  if (!SWIPE_FADE) return;

  const style = document.createElement('style');
  style.textContent = `
    @media (prefers-reduced-motion: no-preference) {
      media-gallery:not(dialog media-gallery, product-card media-gallery) slideshow-slides {
        touch-action: pan-y pinch-zoom;
      }
    }
  `;
  document.head.appendChild(style);

  let start = null;

  document.addEventListener(
    'touchstart',
    (e) => {
      start = null;
      if (reducedMotion.matches || e.touches.length !== 1) return;
      const target = e.target instanceof Element ? e.target : null;
      if (!target || target.closest('model-viewer, video, iframe, button')) return;
      const slideshow = target.closest('slideshow-slides')?.closest('slideshow-component');
      if (!slideshow || !isMainGallerySlideshow(slideshow)) return;
      warmUpGallery(slideshow);
      const t = e.touches[0];
      start = { x: t.clientX, y: t.clientY, slideshow, horizontal: null };
    },
    { passive: true }
  );

  document.addEventListener(
    'touchmove',
    (e) => {
      if (!start) return;
      if (e.touches.length !== 1) {
        start = null;
        return;
      }
      const t = e.touches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (start.horizontal === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        start.horizontal = Math.abs(dx) > Math.abs(dy);
      }
      if (start.horizontal && e.cancelable) e.preventDefault();
    },
    { passive: false }
  );

  document.addEventListener(
    'touchend',
    (e) => {
      if (!start) return;
      const s = start;
      start = null;
      if (!s.horizontal) return;
      const dx = e.changedTouches[0].clientX - s.x;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      if (dx < 0) {
        if (s.slideshow.infinite || !s.slideshow.atEnd) s.slideshow.next();
      } else if (s.slideshow.infinite || !s.slideshow.atStart) {
        s.slideshow.previous();
      }
    },
    { passive: true }
  );

  document.addEventListener('touchcancel', () => (start = null), { passive: true });
}

patch();
patchZoom();
setupSwipeFade();